import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface PropertyGroupProps {
    title: string;
    children: React.ReactNode;
    startOpen?: boolean;
}

export const PropertyGroup = (props: PropertyGroupProps) => {
    const { title, children, startOpen = true } = props;
    const [isOpen, setIsOpen] = React.useState(startOpen);

    const toggleOpen = () => {
        setIsOpen(!isOpen);
    };

    return (
        <div style={styles.group}>
            <motion.div 
                style={styles.header} 
                onClick={toggleOpen}
                whileHover={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderColor: 'rgba(255, 255, 255, 0.15)'
                }}
                whileTap={{ scale: 0.98 }}
            >
                <motion.span 
                    style={styles.arrow}
                    animate={{ rotate: isOpen ? 90 : 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                >
                    ►
                </motion.span>
                <span style={styles.title}>{title}</span>
                <div style={styles.glow} />
            </motion.div>
            
            <AnimatePresence>
                {isOpen && (
                    <motion.div 
                        style={styles.content}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                    >
                        <div style={styles.contentInner}>
                            {children}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const styles = {
    group: {
        marginBottom: '2px',
        borderRadius: '12px',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
    },
    header: {
        cursor: 'pointer',
        fontWeight: '600' as const,
        outline: 'none',
        display: 'flex',
        alignItems: 'center',
        padding: '16px 20px',
        position: 'relative' as const,
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.02) 0%, transparent 100%)',
        transition: 'all 0.2s ease',
        fontSize: '14px',
        letterSpacing: '0.3px',
        color: 'rgba(255, 255, 255, 0.9)',
    },
    arrow: {
        marginRight: '12px',
        fontSize: '10px',
        color: 'rgba(255, 255, 255, 0.7)',
        display: 'inline-block',
        transformOrigin: 'center',
    },
    title: {
        textShadow: '0 0 20px rgba(255, 255, 255, 0.3)',
        position: 'relative' as const,
        zIndex: 2,
    },
    glow: {
        position: 'absolute' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
            radial-gradient(circle at 20% 50%, rgba(147, 112, 219, 0.1) 0%, transparent 70%),
            radial-gradient(circle at 80% 50%, rgba(138, 43, 226, 0.08) 0%, transparent 70%)
        `,
        opacity: 0,
        transition: 'opacity 0.3s ease',
        pointerEvents: 'none' as const,
        zIndex: 1,
    },
    content: {
        overflow: 'hidden',
    },
    contentInner: {
        padding: '16px 20px 20px 20px',
        background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.01) 0%, transparent 100%)',
    }
} as const; 